import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStyles } from "./styles";
import clsx from "clsx";
import {
  Button,
  Grid,
  InputAdornment,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  FormControlLabel,
  Tooltip,
} from "@material-ui/core";
import apiClient from "../../../auth/apiClient";
import ContractService from "../../../services/ContractService";
import FPAPaymentService from "../../../services/FPAPaymentService";
import { useSelector } from "react-redux";
import * as c from "../../../constants";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import Loading from "../../Loading";

const contractService = new ContractService(apiClient);
const fpaPaymentService = new FPAPaymentService(apiClient);

const EditBalanceForm = ({
  toggleModal,
  currentBalance,
  setCurrentBalance,
  contractID,
  entityID,
  fundType,
  fundID,
  setFundID,
  hasCardOnFile,
  accessHolderID
}) => {
  const currentUserID = useSelector((state) => state.user.UserID);
  const enqueueSnackbar = useEnqueueSnackbar();
  const facilityGroupId = useSelector((state) => state.entityScope?.facilityGroupId);
  const scopeAwareFacilityID = facilityGroupId || entityID;


  const classes = useStyles();
  const [updatedBalance, setUpdatedBalance] = useState(
    currentBalance?.toFixed(2)
  );
  const [loading, setLoading] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [showTransactionResult, setShowTransactionResult] = useState(false);

  const manualPayment = "Payment";
  const manualPaymentDisplay = "Manual Payment";
  const onFilePayment = "On File Payment";
  const charge = "Charge";
  const availableAdjustmentTypes = [manualPayment, onFilePayment, charge];

  const EditBalanceSchema = Yup.object().shape({
    amount: Yup.number()
      .typeError("Must be a number")
      .min(0.01, "Enter as a positive number")
      .required("Required"),
    adjustmentType: Yup.string().oneOf(availableAdjustmentTypes),
    note: Yup.string()
      .max(200, "Must be less than 200 characters")
      .required("Required"),
  });

  const defaultValues = {
    amount: 0,
    adjustmentType: manualPayment,
    note: "",
  };

  const methods = useForm({
    resolver: yupResolver(EditBalanceSchema),
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid, errors },
  } = methods;

  const handleTypeChange = (type) => {
    setValue("adjustmentType", type, { shouldValidate: true });
  };

  const watchedAmount = watch("amount");
  const watchedType = watch("adjustmentType");

  useEffect(() => {
    if (!isNaN(watchedAmount) && watchedAmount > 0) {
      let newBalance = 0;
      const adjustment = parseFloat(watchedAmount);

      if (watchedType === charge) {
        newBalance = currentBalance - adjustment;
      } else {
        newBalance = currentBalance + adjustment;
      }

      setUpdatedBalance(parseFloat(newBalance).toFixed(2));
    }
  }, [watchedAmount, watchedType]);

  const updateBalance = async (data) => {
    const { amount, note, adjustmentType } = data;
    let response;

    try {
      if (adjustmentType === onFilePayment) {
        setLoading(true);
        response = await fpaPaymentService.postFPACCOnFilePayment(
          contractID,
          accessHolderID,
          currentUserID,
          amount,
          currentBalance,
          scopeAwareFacilityID,
          note
        );
        
        if (response.data) {
          setTransactionDetails(response.data);
          setShowTransactionResult(true);
          if (response.data.isPaymentSuccessful && !isNaN(response.data?.balance)) {
            setCurrentBalance(response.data.balance);
            setFundID(response.data.fundID);
          }
          return;
        }
      } else {
        const request = {
          amount,
          note,
          adjustmentType: c.FUND_ADJUSTMENT_TYPE[adjustmentType],
          userID: currentUserID,
          fundID,
          entityID: scopeAwareFacilityID,
          contractID,
        };
        response = await contractService.updateFundBalance(
          scopeAwareFacilityID,
          contractID,
          request
        );
      }

      if (!isNaN(response.data?.balance)) {
         setCurrentBalance(response.data.balance);
         setFundID(response.data.fundID);
      }
      
      toggleModal();
    } catch {
      enqueueSnackbar("Failed to update balance", {
        variant: "error",
        tag: "updateFundBalanceError",
      });
      return;
    } finally {
      setLoading(false);
    }
  };

  const renderTransactionDetails = () => {
    if (!transactionDetails) return null;
    
    const isDeclined = transactionDetails.isPaymentSuccessful === false;
    
    return (
      <div className={clsx("transaction-container", classes.transactionContainer)}>
        <div className={clsx("transaction-header", classes.transactionHeader)}>
          {isDeclined ? (
            <Typography variant="h5" color="error">
              Payment Declined
            </Typography>
          ) : (
            <Typography variant="h5" color="primary">
              Approved
            </Typography>
          )}
        </div>
        
        {!isDeclined && (
          <Grid container spacing={2} className={clsx("transaction-details", classes.transactionDetails)}>
            <Grid item xs={6}><Typography align="left"><strong>Transaction Number:</strong></Typography></Grid>
            <Grid item xs={6}><Typography align="left">{transactionDetails.transactionNumber}</Typography></Grid>
            
            <Grid item xs={6}><Typography align="left"><strong>Total Paid:</strong></Typography></Grid>
            <Grid item xs={6}><Typography align="left">${transactionDetails.totalPaid}</Typography></Grid>
            
            <Grid item xs={6}><Typography align="left"><strong>Last 4 of Card:</strong></Typography></Grid>
            <Grid item xs={6}><Typography align="left">{transactionDetails.last4}</Typography></Grid>
            
            <Grid item xs={6}><Typography align="left"><strong>Card Type:</strong></Typography></Grid>
            <Grid item xs={6}><Typography align="left">{transactionDetails.cardType}</Typography></Grid>
            
            <Grid item xs={6}><Typography align="left"><strong>Authorization Code:</strong></Typography></Grid>
            <Grid item xs={6}><Typography align="left">{transactionDetails.authorizationCode}</Typography></Grid>
          </Grid>
        )}
        
        <div className={clsx("transaction-actions", classes.transactionActions)}>
          <Button
            onClick={toggleModal}
            color="primary"
            variant="contained"
            className={clsx("transaction-button", classes.transactionButton)}
          >
            Close
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {loading && (
        <div className={clsx("loading-wrapper", classes.loadingWrapper)}>
          <Loading />
        </div>
      )}
      {showTransactionResult && renderTransactionDetails()}
      {!loading && !showTransactionResult && (
        <form onSubmit={handleSubmit(async (data) => await updateBalance(data))}>
          <div className={clsx("form-content", classes.contentRoot)}>
          <Grid
            container
            spacing={2}
            alignItems="flex-start"
            className={clsx("amount-container", classes.amountContainer)}
          >
            <Grid item xs={6}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Amount"
                    {...field}
                    error={!!errors.amount}
                    helperText={errors.amount && errors.amount.message}
                    type="text"
                    variant="outlined"
                    className={clsx("amount")}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        step: 0.01,
                        "data-testid": "edit-balance-amount",
                      },
                      inputMode: "numeric",
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <RadioGroup
                aria-labelledby="edit-balance-type-radio-button-group"
                onChange={(e, type) => handleTypeChange(type)}
                name="radio-buttons-group"
              >
                <Tooltip title="Use this when accepting cash or check from a customer that is adding funds to their account." placement="right">
                  <FormControlLabel
                    value={manualPayment}
                    control={<Radio />}
                    label={manualPaymentDisplay}
                  />
                </Tooltip>
                {hasCardOnFile && (
                  <Tooltip title="Use this option to charge the credit card on file." placement="right">
                    <FormControlLabel
                      value={onFilePayment}
                      control={<Radio />}
                      label={onFilePayment}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Use this option when manually refunding a debit balance. Note: This does not refund to a credit card." placement="right">
                  <FormControlLabel
                    value={charge}
                    control={<Radio />}
                    label={charge}
                  />
                </Tooltip>
              </RadioGroup>
            </Grid>
          </Grid>
          <Grid
            container
            className={clsx("comment-container", classes.commentContainer)}
          >
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label="Comment"
                  {...field}
                  error={!!errors.comment}
                  helperText={errors.comment && errors.comment.message}
                  type="text"
                  variant="outlined"
                  className={clsx("amount")}
                  InputProps={{
                    inputProps: {
                      "data-testid": "edit-balance-comment",
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid
            container
            className={clsx(
              "balance-update-container",
              classes.balanceAdjustmentContainer
            )}
          >
            <Typography
              data-testid="balance-update-text"
              variant="body1"
              className={clsx(
                "balance-update-text",
                watchedAmount !== 0 ? classes.showBalance : classes.hideBalance
              )}
            >
              New balance will be{" "}
              {updatedBalance < 0
                ? `-$${Math.abs(updatedBalance).toFixed(2)}`
                : `$${updatedBalance}`}
            </Typography>
          </Grid>

          <Grid
            container
            className={clsx("button-container", classes.buttonContainer)}
          >
            <Grid item>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                className={clsx("edit-balance-cancel-button")}
                disabled={isSubmitting || !isValid}
              >
                Submit
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={() => toggleModal()}
                color="secondary"
                variant="contained"
                className={clsx(
                  "edit-balance-cancel-button",
                  classes.cancelButton
                )}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </div>
      </form>
      )}
    </>
  );
};

EditBalanceForm.propTypes = {
  toggleModal: PropTypes.func,
  currentBalance: PropTypes.number,
  setCurrentBalance: PropTypes.func,
  contractID: PropTypes.string,
  entityID: PropTypes.string,
  fundType: PropTypes.number,
  fundID: PropTypes.string,
  setFundID: PropTypes.func,
  hasCardOnFile: PropTypes.bool,
  accessHolderID: PropTypes.string
};

export default EditBalanceForm;
